import {
  AttendanceVerificationMethod,
  type AttendanceVerificationMethodConfig,
} from "@clipboard-health/contract-backend-main";
import { zodResolver } from "@hookform/resolvers/zod";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Collapse, IconButton, Paper, Stack, Switch, Tooltip } from "@mui/material";
import { type WorkplaceExternalIntegration } from "@src/appV2/ExternalIntegrations/api/useGetWorkplaceExternalIntegrations";
import { Text } from "@src/lib/ui-react";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  ATTENDANCE_VERIFICATION_METHOD_DISPLAY,
  getMethodDefaultValues,
  getMethodValidationSchema,
  isExternalIntegrationType,
  type NfcTag,
  type SaveNfcTagLocationParams,
} from "../types";
import { DragHandle } from "./DragHandle";
import { ExternalIntegrationConfig } from "./ExternalIntegrationConfig";
import { NfcTagConfig } from "./NfcTagConfig";
import { TakeAVideoConfig } from "./TakeAVideoConfig";

interface AttendanceVerificationMethodItemProps {
  id: string;
  method: AttendanceVerificationMethodConfig;
  isFirstEnabledMethod: boolean;
  totalMethods: number;
  canToggle: boolean;
  canExpand: boolean;
  disabledReason?: string;
  canReorder: boolean;
  workplaceId: string;
  isCbhAdmin: boolean;
  externalIntegration?: WorkplaceExternalIntegration;
  parentExternalIntegration?: WorkplaceExternalIntegration;
  onSave: (config: AttendanceVerificationMethodConfig) => void;
  nfcRequestList?: NfcTag[];
  onSaveNfcTagLocation?: (params: SaveNfcTagLocationParams) => Promise<void>;
  onDeleteNfcTag?: (tagId: string) => Promise<void>;
  onRequestNfcTags?: (numberOfTags: number) => Promise<void>;
}

function getMethodLabel(method: AttendanceVerificationMethodConfig): string {
  if (isExternalIntegrationType(method.type)) {
    return "External integration";
  }
  return ATTENDANCE_VERIFICATION_METHOD_DISPLAY[method.type]?.label ?? method.type;
}

function getPriorityLabel(isFirstEnabledMethod: boolean): string | null {
  if (isFirstEnabledMethod) {
    return "Highest priority";
  }
  return null;
}

function getDefaultLabel(method: AttendanceVerificationMethodConfig): string | null {
  if (method.type === AttendanceVerificationMethod.GEOFENCE && method.isEnabled) {
    return "Default";
  }
  return null;
}

export function AttendanceVerificationMethodItem(props: AttendanceVerificationMethodItemProps) {
  const {
    id,
    method,
    isFirstEnabledMethod,
    totalMethods,
    canToggle,
    canExpand,
    disabledReason,
    canReorder,
    workplaceId,
    isCbhAdmin,
    externalIntegration,
    parentExternalIntegration,
    onSave,
    nfcRequestList,
    onSaveNfcTagLocation,
    onDeleteNfcTag,
    onRequestNfcTags,
  } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  const hasExpandableContent =
    method.type === AttendanceVerificationMethod.NFC_TAG ||
    isExternalIntegrationType(method.type) ||
    method.type === AttendanceVerificationMethod.TAKE_A_VIDEO ||
    method.type === AttendanceVerificationMethod.AI_TAKE_A_VIDEO;

  const formMethods = useForm({
    resolver: zodResolver(getMethodValidationSchema(method.type)),
    defaultValues: getMethodDefaultValues(method),
    mode: "onChange",
  });

  useEffect(() => {
    formMethods.reset(getMethodDefaultValues(method));
  }, [method, formMethods]);

  const label = getMethodLabel(method);
  const priorityLabel = getPriorityLabel(isFirstEnabledMethod);
  const defaultLabel = getDefaultLabel(method);
  const displayLabel = priorityLabel ?? defaultLabel;

  const handleToggle = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const wantToEnable = event.target.checked;

      if (wantToEnable) {
        const isValid = await formMethods.trigger();
        if (!isValid) {
          setIsExpanded(true);
          return;
        }
      }

      const updatedConfig = { ...method, isEnabled: wantToEnable };
      onSave(updatedConfig);
    },
    [formMethods, method, onSave]
  );

  const handleExpandClick = useCallback(() => {
    setIsExpanded((previous) => !previous);
  }, []);

  function renderExpandedContent() {
    switch (method.type) {
      case AttendanceVerificationMethod.NFC_TAG: {
        return (
          <NfcTagConfig
            nfcRequestList={nfcRequestList ?? []}
            isMethodEnabled={method.isEnabled}
            onSaveLocation={onSaveNfcTagLocation}
            onDeleteTag={onDeleteNfcTag}
            onRequestTags={onRequestNfcTags}
          />
        );
      }
      case AttendanceVerificationMethod.UKG_INTEGRATION:
      case AttendanceVerificationMethod.UKG_READY_INTEGRATION:
      case AttendanceVerificationMethod.ATTENDANCE_ON_DEMAND_INTEGRATION:
      case AttendanceVerificationMethod.MESH_INTEGRATION: {
        return (
          <ExternalIntegrationConfig
            method={method}
            workplaceId={workplaceId}
            isCbhAdmin={isCbhAdmin}
            externalIntegration={externalIntegration}
            parentExternalIntegration={parentExternalIntegration}
            onSave={onSave}
          />
        );
      }
      case AttendanceVerificationMethod.TAKE_A_VIDEO:
      case AttendanceVerificationMethod.AI_TAKE_A_VIDEO: {
        return <TakeAVideoConfig method={method} onSave={onSave} />;
      }
      case AttendanceVerificationMethod.GEOFENCE: {
        return null;
      }
      default: {
        return null;
      }
    }
  }

  return (
    <Paper
      variant="outlined"
      data-testid={`attendance-method-${method.type}`}
      sx={(theme) => ({
        backgroundColor: method.isEnabled ? theme.palette.background.paper : theme.palette.grey[50],
        opacity: method.isEnabled ? 1 : 0.7,
      })}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ padding: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {canReorder && <DragHandle id={id} disabled={totalMethods <= 1 || !canReorder} />}
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Text variant="body1">{label}</Text>
              {displayLabel && (
                <Text variant="caption" color="text.secondary">
                  {displayLabel}
                </Text>
              )}
            </Stack>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={canToggle ? "" : (disabledReason ?? "")} placement="top">
            <span>
              <Switch checked={method.isEnabled} disabled={!canToggle} onChange={handleToggle} />
            </span>
          </Tooltip>
          {canExpand && hasExpandableContent && (
            <IconButton size="small" aria-label={`Expand ${label}`} onClick={handleExpandClick}>
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Stack>
      </Stack>

      {hasExpandableContent && (
        <Collapse in={isExpanded}>
          <Box sx={{ paddingX: 2, paddingBottom: 2 }}>
            <FormProvider {...formMethods}>{renderExpandedContent()}</FormProvider>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}
